/**
 * MenuCanvas — środkowa kolumna buildera menu (ADR-0031).
 *
 * Rekurencyjny render `MenuItem[]` z dnd-kit SortableContext per kontener.
 * Zmiana kolejności WEWNĄTRZ tego samego kontenera (top-level lub sekcja) —
 * przez @dnd-kit (uchwyt na wierszu).
 *
 * Dodawanie z PALETY (2026-09-30) — natywny drag & drop przeglądarki: element
 * przeciągnięty z palety upuszcza się w wskazane miejsce (między pozycjami lub do
 * wnętrza sekcji), ze wskaźnikiem miejsca upuszczenia i błyskiem świeżo dodanej
 * pozycji. Włączane propem `enableDrag` (kreator tenanta + kreator modułów; ITE
 * zostaje bez zmian). Oba mechanizmy nie kolidują — @dnd-kit słucha pointer-eventów
 * na uchwycie, paleta emituje natywne drag-eventy.
 *
 * Zagnieżdżanie w sekcji: upuszczenie z palety NA NAGŁÓWEK sekcji wstawia pozycję na
 * koniec jej wnętrza (dawniej trafiała na poziom główny, pod/nad sekcję). Uchwytem
 * (@dnd-kit) da się też przenosić istniejące pozycje MIĘDZY poziomami — z głównego do
 * sekcji (także pustej, przez jej strefę „Upuść pozycję w tej sekcji") i z powrotem —
 * gdy rodzic poda `onMove`.
 *
 * Toggle collapse: zawsze rozwinięte w canvasie (admin musi widzieć całe drzewko żeby
 * edytować). Pole `MenuItem.collapsed` ustawia tylko default state dla end-userów.
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  AdminMenuItem,
  AdminSystemItemDefinition,
  MenuItem,
  SystemItemDefinition,
} from '@saas/shared';

import {
  computeInsertIndex,
  endPaletteDrag,
  getPaletteDragItem,
  hasPaletteDrag,
} from '@/lib/menu-dnd';
import { findContainerOf, findInTree } from '@/lib/menu-tree';

import { MenuItemBlock, type MenuItemViewLabels } from './MenuItemBlock';
import type { MenuItemInlineEditorProps } from './MenuItemInlineEditor';
import { MenuItemInlineEditor, resolveMenuItemKartotekaName } from './MenuItemInlineEditor';

/**
 * Minimum-shape dla system item lookup (label fallback w MenuItemBlock).
 * Wystarcza dla tenant SystemItemDefinition + admin AdminSystemItemDefinition.
 */
type AnySystemItemDefinition = Pick<SystemItemDefinition, 'labelI18nKey' | 'defaultLabel'> &
  Partial<Pick<SystemItemDefinition, 'iconName' | 'path'>>;

/** Prefiks id strefy upuszczenia @dnd-kit w PUSTEJ sekcji (przenoszenie uchwytem). */
const SECTION_DROP_PREFIX = '__section-drop__:';

/** Cel upuszczenia z palety: kontener (sekcja / null=top-level) + indeks wstawienia. */
interface DropTarget {
  containerId: string | null;
  index: number;
}

export interface MenuCanvasProps {
  hierarchy: MenuItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  /** Wywoływane gdy reorder zakończony. parentId=null = top-level. */
  onReorder: (parentId: string | null, fromIndex: number, toIndex: number) => void;
  /**
   * Przeniesienie istniejącej pozycji do INNEGO kontenera (z głównego poziomu do sekcji
   * i z powrotem). containerId=null = top-level, index = miejsce wstawienia w kontenerze
   * docelowym. Brak = przeciąganie między poziomami nic nie robi (jak dawniej).
   */
  onMove?: (id: string, containerId: string | null, index: number) => void;
  /**
   * Dodanie pozycji z palety przez upuszczenie w konkretne miejsce.
   * Gdy podane wraz z `enableDrag` — płótno przyjmuje natywne drag & drop z palety.
   */
  onAddAt?: (containerId: string | null, index: number, item: MenuItem | AdminMenuItem) => void;
  /** Włącza przyjmowanie pozycji przeciąganych z palety. Default false (ITE bez zmian). */
  enableDrag?: boolean;
  /**
   * i18n key prefix (np. 'tenant.menuLayoutBuilder' albo 'admin.menuLayoutBuilder').
   * Default: 'tenant.menuLayoutBuilder' (back-compat dla istniejących callsiteów).
   */
  i18nPrefix?: string;
  /**
   * Mapa system item definicji przekazywana do MenuItemBlock (fallback label + ikona).
   * Default: undefined → MenuItemBlock użyje własny default (tenant SYSTEM_ITEMS).
   */
  systemItemDefinitions?: ReadonlyMap<string, AnySystemItemDefinition | AdminSystemItemDefinition>;
  /**
   * Liczy etykiety widoków (chip layoutu formularza/listy) dla pozycji menu type='module'.
   * Zwraca null gdy pozycja nie ma etykiet (inne typy / brak dopasowania encji).
   * Default undefined → brak chipów (admin scope, tenant menu bez szablonu).
   */
  resolveViewLabels?: (item: MenuItem) => MenuItemViewLabels | null;
  /**
   * Props do MenuItemInlineEditor — gdy podane, akordeon renderuje się pod zaznaczonym wierszem.
   * Wymagane: templateCode + entities + callbacki onAssignForm/onDesignForm/onDesignList/onCreateForm.
   */
  inlineEditorProps?: Omit<MenuItemInlineEditorProps, 'item' | 'onChange' | 'i18nPrefix'>;
  /** onChange dla MenuItemInlineEditor (properties). Wymagane gdy inlineEditorProps podane. */
  onChangeItem?: (next: MenuItem) => void;
  /**
   * Opcjonalny callback — wywoływany gdy kliknięto już zaznaczoną pozycję (toggle-deselect).
   * Przekazywany do MenuItemBlock.onDeselect. Gdy niepodany — re-klik nie odznacza (MenuBuilderShell).
   */
  onDeselect?: () => void;
}

export function MenuCanvas({
  hierarchy,
  selectedId,
  onSelect,
  onDelete,
  onReorder,
  onMove,
  onAddAt,
  enableDrag = false,
  i18nPrefix = 'tenant.menuLayoutBuilder',
  systemItemDefinitions,
  resolveViewLabels,
  inlineEditorProps,
  onChangeItem,
  onDeselect,
}: MenuCanvasProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const dndEnabled = enableDrag && !!onAddAt;
  const treeRef = useRef<HTMLDivElement>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [isOver, setIsOver] = useState(false);
  // Błysk świeżo dodanej pozycji (dowolne źródło: paleta, plus, duplikat) — wykrywamy
  // pojawienie się dokładnie jednego nowego id między renderami. Pierwsze zapełnienie
  // (baseline) nie błyska. Wykrycie i gaszenie rozdzielone na dwa efekty: inaczej zmiana
  // drzewa w ciągu 1,6 s (reorder/usunięcie/edycja) czyściłaby timer bez ustawienia
  // nowego i błysk zostawałby zawieszony na stałe.
  const [flashId, setFlashId] = useState<string | null>(null);
  const prevIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const ids = collectIds(hierarchy);
    const prev = prevIdsRef.current;
    // Baseline ustawiamy dopiero na pierwszej NIEpustej hierarchii — canvas montuje się
    // z pustym drzewem i dopiero potem dostaje dane; bez tego load [] → N pozycji
    // policzyłby wszystkie jako „dodane" i menu o jednej pozycji błysnęłoby na wejściu.
    if (prev === null) {
      if (ids.size > 0) prevIdsRef.current = ids;
      return;
    }
    prevIdsRef.current = ids;
    const added: string[] = [];
    ids.forEach((id) => {
      if (!prev.has(id)) added.push(id);
    });
    if (added.length === 1) setFlashId(added[0]!);
  }, [hierarchy]);
  useEffect(() => {
    if (!flashId) return;
    const timer = window.setTimeout(() => setFlashId(null), 1600);
    return () => window.clearTimeout(timer);
  }, [flashId]);

  // Higiena: gdy płótno znika w trakcie przeciągania, wyczyść współdzielony stan drag,
  // żeby nie przeciekł do kolejnej instancji buildera / sesji.
  useEffect(() => endPaletteDrag, []);

  const computeDropTarget = (clientX: number, clientY: number): DropTarget | null => {
    const tree = treeRef.current;
    if (!tree) return null;
    const point = document.elementFromPoint(clientX, clientY);
    // Nad nagłówkiem sekcji → do jej wnętrza, na koniec (intuicja „wrzucam DO sekcji").
    const sectionHeader = point instanceof Element ? point.closest('[data-section-header]') : null;
    if (sectionHeader && tree.contains(sectionHeader)) {
      const sectionId = sectionHeader.getAttribute('data-section-header')!;
      const section = findInTree(hierarchy, sectionId);
      if (section) return { containerId: sectionId, index: section.children?.length ?? 0 };
    }
    const containerEl = point instanceof Element ? point.closest('[data-drop-container]') : null;
    if (!containerEl || !tree.contains(containerEl)) {
      // Poza jakimkolwiek kontenerem, ale nad płótnem → koniec top-levelu.
      return { containerId: null, index: hierarchy.length };
    }
    const raw = containerEl.getAttribute('data-container-id');
    const containerId = raw === '__root' ? null : raw;
    const rects = Array.from(
      containerEl.querySelectorAll<HTMLElement>(':scope > [data-drop-row]'),
    ).map((row) => row.getBoundingClientRect());
    return { containerId, index: computeInsertIndex(rects, clientY) };
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!dndEnabled || !hasPaletteDrag()) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsOver(true);
    setDropTarget(computeDropTarget(e.clientX, e.clientY));
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!dndEnabled) return;
    // Ignoruj przejścia między dziećmi płótna — reaguj tylko na realne opuszczenie.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsOver(false);
    setDropTarget(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!dndEnabled) return;
    const item = getPaletteDragItem();
    setIsOver(false);
    // Bierzemy cel z OSTATNIEGO dragover (to, co user widział jako wskaźnik). Ponowne
    // liczenie tutaj byłoby na DOM-ie przesuniętym przez wstawiony wskaźnik (drop-line),
    // więc mogłoby wskazać indeks o jeden inny niż pokazana linia. Fallback: policz teraz.
    const target = dropTarget ?? computeDropTarget(e.clientX, e.clientY);
    setDropTarget(null);
    if (!item) return;
    e.preventDefault();
    const resolved = target ?? { containerId: null, index: hierarchy.length };
    onAddAt?.(resolved.containerId, resolved.index, item);
    endPaletteDrag();
  };

  const dropHandlers = dndEnabled
    ? { onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop }
    : {};

  return (
    <section
      className={[
        'flex flex-1 flex-col gap-2 overflow-y-auto bg-bg-secondary p-4 transition',
        isOver ? 'ring-2 ring-inset ring-accent-300' : '',
      ].join(' ')}
      data-testid="menu-canvas"
      {...dropHandlers}
    >
      {hierarchy.length === 0 ? (
        <EmptyState i18nPrefix={i18nPrefix} showDropHint={dndEnabled} isOver={isOver} />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(ev) => handleDragEnd(ev, hierarchy, onReorder, onMove)}
        >
          <div ref={treeRef}>
            <ContainerLevel
              items={hierarchy}
              parentId={null}
              selectedId={selectedId}
              onSelect={onSelect}
              onDelete={onDelete}
              i18nPrefix={i18nPrefix}
              systemItemDefinitions={systemItemDefinitions}
              resolveViewLabels={resolveViewLabels}
              inlineEditorProps={inlineEditorProps}
              onChangeItem={onChangeItem}
              onDeselect={onDeselect}
              dndEnabled={dndEnabled}
              dropTarget={dropTarget}
              flashId={flashId}
            />
          </div>
        </DndContext>
      )}
    </section>
  );
}

function EmptyState({
  i18nPrefix,
  showDropHint,
  isOver,
}: {
  i18nPrefix: string;
  showDropHint: boolean;
  isOver: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={[
        'flex flex-1 items-center justify-center rounded-lg border-2 border-dashed p-8 text-center transition',
        isOver ? 'border-accent-400 bg-accent-50' : 'border-border-secondary bg-bg-primary',
      ].join(' ')}
      data-drop-container
      data-container-id="__root"
    >
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-text-secondary">{t(`${i18nPrefix}.empty.title`)}</p>
        <p className="text-xs text-text-muted">{t(`${i18nPrefix}.empty.body`)}</p>
        {showDropHint ? (
          <p className="text-xs text-text-muted">
            {t(`${i18nPrefix}.dnd.emptyDropHint`, {
              defaultValue: 'Przeciągnij element z palety tutaj albo dodaj plusem.',
            })}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// wskaźnik miejsca upuszczenia
// ---------------------------------------------------------------------------

function DropLine() {
  return (
    <div className="relative my-0.5 h-0.5 rounded bg-accent-500" data-testid="menu-drop-line">
      <span className="absolute -left-1 -top-[3px] h-2 w-2 rounded-full bg-accent-500" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// recursive level
// ---------------------------------------------------------------------------

function ContainerLevel({
  items,
  parentId,
  selectedId,
  onSelect,
  onDelete,
  i18nPrefix,
  systemItemDefinitions,
  resolveViewLabels,
  inlineEditorProps,
  onChangeItem,
  onDeselect,
  dndEnabled,
  dropTarget,
  flashId,
}: {
  items: MenuItem[];
  parentId: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  i18nPrefix?: string;
  systemItemDefinitions?: ReadonlyMap<string, AnySystemItemDefinition | AdminSystemItemDefinition>;
  resolveViewLabels?: (item: MenuItem) => MenuItemViewLabels | null;
  inlineEditorProps?: Omit<MenuItemInlineEditorProps, 'item' | 'onChange' | 'i18nPrefix'>;
  onChangeItem?: (next: MenuItem) => void;
  onDeselect?: () => void;
  dndEnabled?: boolean;
  dropTarget?: DropTarget | null;
  flashId?: string | null;
}) {
  const targetsHere = !!dropTarget && dropTarget.containerId === (parentId ?? null);
  return (
    <SortableContext
      items={items.map((i) => i.id)}
      strategy={verticalListSortingStrategy}
      id={parentId ?? '__root'}
    >
      <div
        className="flex flex-col gap-1"
        data-drop-container
        data-container-id={parentId ?? '__root'}
      >
        {items.map((item, idx) => (
          <Fragment key={item.id}>
            {targetsHere && dropTarget!.index === idx ? <DropLine /> : null}
            <SortableItem
              item={item}
              selectedId={selectedId}
              onSelect={onSelect}
              onDelete={onDelete}
              i18nPrefix={i18nPrefix}
              systemItemDefinitions={systemItemDefinitions}
              resolveViewLabels={resolveViewLabels}
              inlineEditorProps={inlineEditorProps}
              onChangeItem={onChangeItem}
              onDeselect={onDeselect}
              dndEnabled={dndEnabled}
              dropTarget={dropTarget}
              flashId={flashId}
            />
          </Fragment>
        ))}
        {targetsHere && dropTarget!.index >= items.length ? <DropLine /> : null}
      </div>
    </SortableContext>
  );
}

function SortableItem({
  item,
  selectedId,
  onSelect,
  onDelete,
  i18nPrefix,
  systemItemDefinitions,
  resolveViewLabels,
  inlineEditorProps,
  onChangeItem,
  onDeselect,
  dndEnabled,
  dropTarget,
  flashId,
}: {
  item: MenuItem;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  i18nPrefix?: string;
  systemItemDefinitions?: ReadonlyMap<string, AnySystemItemDefinition | AdminSystemItemDefinition>;
  resolveViewLabels?: (item: MenuItem) => MenuItemViewLabels | null;
  inlineEditorProps?: Omit<MenuItemInlineEditorProps, 'item' | 'onChange' | 'i18nPrefix'>;
  onChangeItem?: (next: MenuItem) => void;
  onDeselect?: () => void;
  dndEnabled?: boolean;
  dropTarget?: DropTarget | null;
  flashId?: string | null;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const isFlashing = flashId === item.id;
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isFlashing
      ? `${transition ?? ''}${transition ? ', ' : ''}background-color 1.5s ease-out, box-shadow 1.5s ease-out`
      : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isExpanded = selectedId === item.id;

  const inlineEditor =
    isExpanded && inlineEditorProps && onChangeItem ? (
      // Akordeon z własnym scrollem i ograniczoną wysokością — rozwinięty edytor
      // nie spycha reszty listy w dół (redukcja scrolla całego ekranu); długi edytor
      // przewija się wewnątrz tej ramki zamiast rozpychać drzewo.
      <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
        <MenuItemInlineEditor
          {...inlineEditorProps}
          item={item}
          onChange={(next) => onChangeItem(next as MenuItem)}
          i18nPrefix={i18nPrefix}
        />
      </div>
    ) : undefined;

  // Nazwa kartoteki dla badge w wierszu — z entities/templateCode (jeśli przekazane).
  // Tenant (2026-07-02): custom resolveEntitySlug wygrywa (moduleCode bez prefiksu).
  // MenuBuilderShell bez inlineEditorProps → undefined → brak badge.
  const customEntitySlug = inlineEditorProps?.resolveEntitySlug
    ? inlineEditorProps.resolveEntitySlug(item)
    : null;
  const kartotekaName = inlineEditorProps
    ? inlineEditorProps.resolveEntitySlug
      ? ((inlineEditorProps.entities ?? []).find((e) => e.slug === customEntitySlug)?.name ??
        undefined)
      : (resolveMenuItemKartotekaName(
          item,
          inlineEditorProps.templateCode,
          inlineEditorProps.entities ?? [],
        ) ?? undefined)
    : undefined;

  const isSection = item.type === 'section';
  const hasChildren = !!(item.children && item.children.length > 0);
  // Sekcja bez dzieci: gdy DnD włączone, renderujemy pustą strefę wewnątrz, żeby dało
  // się upuścić do środka (i pokazać wskaźnik). Bez DnD zostaje jak było.
  const renderChildrenContainer = isSection && (hasChildren || !!dndEnabled);

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-drop-row
      data-row-id={item.id}
      className={isFlashing ? 'rounded-md bg-accent-50 ring-2 ring-accent-400' : undefined}
    >
      {/* Nagłówek sekcji jest celem upuszczenia „do wnętrza" (computeDropTarget). */}
      <div data-section-header={isSection ? item.id : undefined}>
        <MenuItemBlock
          item={item}
          isSelected={isExpanded}
          isExpanded={isExpanded}
          inlineEditorSlot={inlineEditor}
          onSelect={onSelect}
          onDelete={onDelete}
          onDeselect={onDeselect}
          dragHandleProps={{ ...attributes, ...listeners }}
          viewLabels={resolveViewLabels?.(item) ?? undefined}
          kartotekaName={kartotekaName}
          i18nPrefix={i18nPrefix}
          systemItemDefinitions={systemItemDefinitions}
        />
      </div>
      {renderChildrenContainer ? (
        <div className="ml-6 mt-1 border-l border-border-secondary pl-3">
          {hasChildren ? (
            <ContainerLevel
              items={item.children!}
              parentId={item.id}
              selectedId={selectedId}
              onSelect={onSelect}
              onDelete={onDelete}
              i18nPrefix={i18nPrefix}
              systemItemDefinitions={systemItemDefinitions}
              resolveViewLabels={resolveViewLabels}
              inlineEditorProps={inlineEditorProps}
              onChangeItem={onChangeItem}
              onDeselect={onDeselect}
              dndEnabled={dndEnabled}
              dropTarget={dropTarget}
              flashId={flashId}
            />
          ) : (
            <EmptySectionDropZone
              sectionId={item.id}
              i18nPrefix={i18nPrefix ?? 'tenant.menuLayoutBuilder'}
              active={!!dropTarget && dropTarget.containerId === item.id}
              t={t}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Strefa upuszczenia „do wnętrza" pustej sekcji (tylko przy włączonym DnD z palety). */
function EmptySectionDropZone({
  sectionId,
  i18nPrefix,
  active,
  t,
}: {
  sectionId: string;
  i18nPrefix: string;
  active: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  // Ta sama strefa przyjmuje też pozycję przenoszoną uchwytem (@dnd-kit) — pusta sekcja nie
  // ma wierszy, nad którymi dałoby się ją upuścić.
  const { setNodeRef, isOver } = useDroppable({ id: `${SECTION_DROP_PREFIX}${sectionId}` });
  return (
    <div
      ref={setNodeRef}
      data-drop-container
      data-container-id={sectionId}
      data-testid={`menu-empty-section-drop-${sectionId}`}
      className={[
        'rounded-md border border-dashed px-2 py-2 text-center text-[11px] transition',
        active || isOver
          ? 'border-accent-400 bg-accent-50 text-text-secondary'
          : 'border-border-secondary text-text-muted',
      ].join(' ')}
    >
      {active ? <DropLine /> : null}
      {t(`${i18nPrefix}.dnd.emptySectionDrop`, { defaultValue: 'Upuść pozycję w tej sekcji' })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function collectIds(items: MenuItem[]): Set<string> {
  const set = new Set<string>();
  const walk = (arr: MenuItem[]) => {
    for (const it of arr) {
      set.add(it.id);
      if (it.children && it.children.length > 0) walk(it.children);
    }
  };
  walk(items);
  return set;
}

// ---------------------------------------------------------------------------
// drag handler (reorder @dnd-kit)
// ---------------------------------------------------------------------------

export function handleDragEnd(
  event: DragEndEvent,
  hierarchy: MenuItem[],
  onReorder: (parentId: string | null, fromIndex: number, toIndex: number) => void,
  onMove?: (id: string, containerId: string | null, index: number) => void,
): void {
  const { active, over } = event;
  if (!over || active.id === over.id) return;
  const activeId = String(active.id);
  const overId = String(over.id);

  // Strefa pustej sekcji → do jej wnętrza.
  if (overId.startsWith(SECTION_DROP_PREFIX)) {
    const sectionId = overId.slice(SECTION_DROP_PREFIX.length);
    if (sectionId !== activeId) onMove?.(activeId, sectionId, 0);
    return;
  }

  const fromCtx = findContainerOf(hierarchy, activeId);
  const toCtx = findContainerOf(hierarchy, overId);
  if (fromCtx === null || toCtx === null) return;

  if (fromCtx.parentId !== toCtx.parentId) {
    // Inny kontener (główny poziom ↔ sekcja): wstaw w miejsce pozycji, nad którą upuszczono.
    const toIndex = toCtx.items.findIndex((i) => i.id === overId);
    if (toIndex === -1) return;
    onMove?.(activeId, toCtx.parentId, toIndex);
    return;
  }

  const arr = fromCtx.items;
  const fromIndex = arr.findIndex((i) => i.id === activeId);
  const toIndex = arr.findIndex((i) => i.id === overId);
  if (fromIndex === -1 || toIndex === -1) return;

  onReorder(fromCtx.parentId, fromIndex, toIndex);
}
